// Exact threshold arithmetic (ENGINE §1 N-10: every threshold is judged on the UNROUNDED value).
//
// T-256 moved this file from `src/engine/exact.ts` into `src/num`, which is where ENGINE §1's numeric policy
// already lives (ARCH §8.1: `num` / `periods` / `metrics` are the public arithmetic the engine shares — the
// engine imports them, never the reverse). Nothing about the rules changed; what changed is that a PUBLIC
// module may now judge a threshold exactly. `@sl/core/shares` is the first one that has to (D-126): the
// ±10-point share label is a BigInt cross-multiplication and is wrong any other way, so leaving `exact`
// inside the engine would have forced either a second copy of it or an app-visible engine import.
// `@sl/core/engine` still re-exports every name below, so no engine caller moved.
// Doubles cannot do that at a boundary — 112.60 ÷ 3 and 2 × (168.90 ÷ 9) differ by one ulp though they are the
// same number, and 0.7 % is "29.999999999999993 %" below 1.00 % — and C#'s `decimal` cannot either once it has
// divided (18.7666…7 × 2 ≠ 37.5333…3). So the engine never divides before it compares: money is scaled to a
// 6-decimal integer (N-1: a sum carries the currency's ≤ 3 minor decimals, derived money 6), counts are integers,
// and a ratio is compared by cross-multiplying in BigInt. The C# mirror does the same (Exact.cs), which is what
// makes the two sides agree on every golden boundary case. Values shown to a user are still the plain doubles
// of `src/metrics`, rounded once on the way out (N-0).

import { shiftExponent } from './num';

/** Decimal places every money amount and factor is scaled to before an exact comparison. */
export const EXACT_SCALE = 6;

/** A number with at most `EXACT_SCALE` decimals as an exact integer (`90.21` → `90210000n`). */
export function units(x: number): bigint {
  if (!Number.isFinite(x)) throw new RangeError(`exact: ${x} is not a finite number`);
  return BigInt(Math.round(shiftExponent(x, EXACT_SCALE)));
}

/** The number the scaled integer denotes — the double nearest to the decimal (`350010000n` → `350.01`). */
export function fromUnits(u: bigint): number {
  return Number(u) / 10 ** EXACT_SCALE;
}

/** An exact rational `num ÷ den` with `den > 0` — a plain NUMBER (scale-free): a cost per result, a percentage, a factor. */
export interface Ratio {
  num: bigint;
  den: bigint;
}

export function ratio(num: bigint, den: bigint): Ratio {
  if (den <= 0n) throw new RangeError('exact: a ratio needs a positive denominator');
  return { num, den };
}

/** The number `x` (≤ 6 decimals) as an exact ratio. */
export function asRatio(x: number): Ratio {
  return ratio(units(x), units(1));
}

/** The largest integer a double represents exactly — the bound on an EXACT `Number(bigint)` conversion. */
const EXACT_BIGINT = BigInt(Number.MAX_SAFE_INTEGER); // 2^53 − 1

function gcd(a: bigint, b: bigint): bigint {
  let x = a < 0n ? -a : a;
  let y = b < 0n ? -b : b;
  while (y !== 0n) [x, y] = [y, x % y];
  return x === 0n ? 1n : x;
}

/**
 * The double nearest enough to `r` for display (never used to decide a threshold).
 *
 * `Number(num) / Number(den)` is the CORRECTLY-ROUNDED value of the exact rational while both conversions are
 * exact — which they are while both parts are under 2^53 (ENGINE §8 step 4's stated limit; true of every figure
 * this product carries, where the largest denominators are ~1e11). T-376's rider closes the gap rather than
 * restating it: **a ratio whose parts exceed that bound is REDUCED here, at the conversion**, so the limit is a
 * property of this function and not a comment that a future caller has to have read.
 *
 * `ratio()` itself still does not reduce, deliberately: `addRatios` / `scaleRatio` / `medianRatio` compose
 * rationals thousands of times per request on the engine's hot path, and a gcd on every construction would buy
 * nothing for a bound nothing approaches. Reduction belongs where the exactness is actually spent.
 *
 * Beyond even a reduced ratio's bound (two coprime parts both over 2^53) each conversion is within half an ulp,
 * so the quotient is within about two — and no threshold is ever decided from this value (`compareRatios` is).
 */
export function ratioValue(r: Ratio): number {
  let { num, den } = r;
  if (num > EXACT_BIGINT || num < -EXACT_BIGINT || den > EXACT_BIGINT) {
    const g = gcd(num, den);
    num /= g;
    den /= g;
  }
  return Number(num) / Number(den);
}

/** `−1`, `0` or `1` as `a` is below, equal to or above `b`. */
export function compareRatios(a: Ratio, b: Ratio): -1 | 0 | 1 {
  const l = a.num * b.den;
  const r = b.num * a.den;
  return l < r ? -1 : l > r ? 1 : 0;
}

/** `a × (byNum ÷ byDen)`. */
export function scaleRatio(a: Ratio, byNum: bigint, byDen = 1n): Ratio {
  return ratio(a.num * byNum, a.den * byDen);
}

export function addRatios(a: Ratio, b: Ratio): Ratio {
  return ratio(a.num * b.den + b.num * a.den, a.den * b.den);
}

/**
 * The median of exact ratios — the middle value of an odd count, the mean of the two middle values of an even
 * one (ENGINE §9 (b); QA's `ws-b-median-even-count`). `null` for no values.
 */
export function medianRatio(values: readonly Ratio[]): Ratio | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort(compareRatios);
  const n = sorted.length;
  if (n % 2 === 1) return sorted[(n - 1) / 2]!;
  return scaleRatio(addRatios(sorted[n / 2 - 1]!, sorted[n / 2]!), 1n, 2n);
}

/**
 * Cost per result as an exact ratio: `spend ÷ count` (× 1,000 for a per-1,000 type — ENGINE §3, §5.5). `null`
 * for no results (N-5). `spend` is the TYPE-SCOPED spend in major units.
 */
export function costRatio(spend: number, count: number, per1000: boolean): Ratio | null {
  if (!Number.isInteger(count) || count <= 0) return null;
  return ratio(units(spend) * (per1000 ? 1000n : 1n), BigInt(count) * units(1));
}

/**
 * BUG-118 / D-064 / D-243: the ±`stepPct` edge of a budget rounded INWARD to a whole currency unit —
 * `floor(budget × (1 + step))` for a raise, `ceil(budget × (1 − step))` for a cut — exact on 6-decimal units,
 * never a float product. $25 → 30 / 20; $13 → 15 (not 16, which would be a 23 % step); $22 → 18. The ONE
 * function behind the alerts engine's step and the allocation map's: two spellings were how the map came to
 * propose a step the product warns against for budgets under about $22.
 */
export function inwardStep(budget: number, stepPct: number, direction: 'raise' | 'lower'): number {
  const u = units(budget);
  const factor = BigInt(100 + (direction === 'raise' ? stepPct : -stepPct));
  const scaled = u * factor; // budget × (100 ± step), in 6-decimal units × 100
  const den = 100n * units(1);
  const floorDiv = (a: bigint, b: bigint): bigint => (a >= 0n ? a / b : -((-a + b - 1n) / b));
  const ceilDiv = (a: bigint, b: bigint): bigint => (a >= 0n ? (a + b - 1n) / b : -(-a / b));
  return Number(direction === 'raise' ? floorDiv(scaled, den) : ceilDiv(scaled, den));
}
